import React, { useState } from 'react';

import { Box, Button } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

import { ITableDateRangeFilterProps } from 'src/components/common/types/tableTypes';

const filterKeyStart = 'start_date';
const filterKeyEnd = 'end_date';

export const TableDateRangeFilter: React.FC<ITableDateRangeFilterProps> = ({
	queryState,
	onQueryChange,
}) => {
	const [startDate, setStartDate] = useState<Dayjs | null>(
		queryState.dateFilters?.start_date
			? dayjs(queryState.dateFilters.start_date)
			: null,
	);
	const [endDate, setEndDate] = useState<Dayjs | null>(
		queryState.dateFilters?.end_date
			? dayjs(queryState.dateFilters.end_date)
			: null,
	);

	const handleDateChange = (date: Dayjs | null, type: 'start' | 'end') => {
		if (type === 'start') setStartDate(date);
		else setEndDate(date);

		const newStart = type === 'start' ? date : startDate;
		const newEnd = type === 'end' ? date : endDate;

		// Only trigger update if both dates are selected
		if (newStart && newEnd) {
			onQueryChange({
				...queryState,
				page: 0,
				dateFilters: {
					[filterKeyStart]: newStart.format('YYYY-MM-DD'),
					[filterKeyEnd]: newEnd.format('YYYY-MM-DD'),
				},
			});
		}
	};

	const handleClear = () => {
		setStartDate(null);
		setEndDate(null);
		onQueryChange({
			...queryState,
			page: 0,
			dateFilters: undefined,
		});
	};

	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'flex-end',
				gap: 2,
				alignItems: 'center',
			}}
		>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DatePicker
					label='Start date'
					value={startDate}
					maxDate={endDate || undefined}
					onChange={(date) => handleDateChange(date, 'start')}
					slotProps={{
						textField: { size: 'small', fullWidth: true },
					}}
				/>
			</LocalizationProvider>
			<Box sx={{ display: { xs: 'none', sm: 'block' } }}>-</Box>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DatePicker
					label='End date'
					value={endDate}
					minDate={startDate || undefined}
					onChange={(date) => handleDateChange(date, 'end')}
					slotProps={{
						textField: { size: 'small', fullWidth: true },
					}}
				/>
			</LocalizationProvider>
			{(startDate || endDate) && (
				<Button
					variant='outlined'
					color='error'
					onClick={handleClear}
					sx={{ minWidth: 'fit-content', whiteSpace: 'nowrap' }}
				>
					CLEAR DATES
				</Button>
			)}
		</Box>
	);
};
